/**
 * AuthenticatedOnlyRoute - Guards routes that only require authentication
 *
 * Unlike ProtectedRoute, this does NOT check for organization selection.
 * Use this for routes like /auth/accept-tos that need authentication
 * but should render before organization selection is required.
 *
 * @see GW-450 - Fix TOS acceptance page redirect issue
 * @see GW-443 - Wait for checkAuth to complete before trusting isAuthenticated
 */

import { isOperationLoading, hasOperationLoaded } from '@gw/ui';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuthStore } from '@/stores/store';

export function AuthenticatedOnlyRoute() {
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading, hasLoaded: authHasLoaded } = useAuthStore();

  // Check if auth check is in progress
  const isCheckingAuth = isOperationLoading(authLoading, 'checkAuth');
  // GW-443: Check if auth check has completed (not just "not loading")
  const hasCheckedAuth = hasOperationLoaded(authHasLoaded, 'checkAuth');

  // GW-443: Show loading until checkAuth has COMPLETED verification
  // This prevents decisions based on stale persisted isAuthenticated value
  if (isCheckingAuth || !hasCheckedAuth) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
          <p className="text-muted-foreground text-sm">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    const returnTo = location.pathname + location.search;
    // Don't append redirect param for /auth/login itself or root path
    const shouldAppendRedirect = returnTo !== '/' && !returnTo.startsWith('/auth/login');
    const loginPath = shouldAppendRedirect
      ? `/auth/login?redirect=${encodeURIComponent(returnTo)}`
      : '/auth/login';
    return <Navigate to={loginPath} replace />;
  }

  // Authenticated - render children (no org check)
  return <Outlet />;
}
